// Auto-billing for prescriptions: when a vet creates a NEW prescription on a
// sale, every product the clinic has flagged `addOnPrescription` is added as a
// sale line, once per sale. Gated by customer.featureFlags.autoAddPrescriptionProduct.
// Spec: docs/superpowers/specs/2026-09-23-online-prescription-orders-design.md
//
// The line builder is pure and test-covered; the async wrapper is thin store
// glue used by the prescription drawer callbacks (listPrescriptionMini.js and
// listAnimalMini.js). It must never throw into those callbacks.
import md5 from 'crypto-js/md5';

export function isAutoAddPrescriptionProductEnabled(customer) {
    const featureFlags = (customer && customer.featureFlags) || {};
    return parseInt(featureFlags.autoAddPrescriptionProduct, 10) === 1;
}

export function buildPrescriptionProductLines({
    products = [],
    existingProducts = [],
    animal,
    user = {},
} = {}) {
    if (!animal || !animal.id) {
        return [];
    }
    const animalRef = { id: animal.id, name: animal.name };
    const lines = [];
    for (let i = 0, l = products.length; i < l; i += 1) {
        const product = products[i];
        if (product && product.id) {
            const alreadyOnSale = existingProducts.some(p => p && p.id === product.id)
                || lines.some(p => p.id === product.id);
            if (!alreadyOnSale) {
                const createdDate = new Date();
                lines.push({
                    ...product,
                    qty: 1,
                    createdDate,
                    md5: md5(JSON.stringify({ id: product.id, createdDate, animal: animalRef, i })).toString(),
                    saleUser: user.id,
                    saleDepartment: user.currentCustomerDepartment,
                    animal: animalRef,
                });
            }
        }
    }
    return lines;
}

// Returns the number of lines added. Never throws.
export async function addPrescriptionProductLines({
    saleStore, productStore, userStore, animalId,
} = {}) {
    try {
        if (!saleStore || !productStore || !userStore) {
            return 0;
        }
        const { customer, user } = userStore;
        if (!isAutoAddPrescriptionProductEnabled(customer)) {
            return 0;
        }
        const { newSale } = saleStore;
        if (!newSale || !newSale.id) {
            return 0;
        }
        const animal = (newSale.animals || []).find(a => a && a.id === animalId);
        if (!animal) {
            return 0;
        }
        const products = await productStore.load({
            query: { addOnPrescription: 1 },
            limit: 50,
            skipUpdate: true,
        });
        const lines = buildPrescriptionProductLines({
            products: Array.isArray(products) ? products : [],
            existingProducts: newSale.products || [],
            animal,
            user,
        });
        if (lines.length === 0) {
            return 0;
        }
        saleStore.updateObjectKeyValue('newSale', 'products', [...(newSale.products || []), ...lines]);
        saleStore.updateObjectKeyValue('newSale', 'productAdded', true);
        return lines.length;
    } catch (error) {
        console.error('addPrescriptionProductLines failed:', error);
        return 0;
    }
}

export default {
    isAutoAddPrescriptionProductEnabled,
    buildPrescriptionProductLines,
    addPrescriptionProductLines,
};
